// MeiosPagamento.styles.ts
//
// Anatomia igual às outras sections de Lojas (Subsidiary, Ambients, Items):
// FieldsContainer como raiz, Header/HeaderContent/HeaderTitle/HeaderSubtitle,
// LoadingContainer/LoadingSpinner, EmptyState.
//
// Tamanhos só na escada de tokens (`var(--sp-N)` / `var(--fs-N)`); o que não cai
// nos 13 valores de --sp- vira `calc(var(--u) * N)`. Cores só de
// `props.theme.colors` — que hoje tem primary, background, text, selection e
// modalColor (o `colors.border` usado no repo resolve undefined; é o padrão
// vigente e não é consertado aqui).
import styled, { keyframes } from "styled-components";

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(-10px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0%, 100% { opacity: 1; }
  50% { opacity: 0.5; }
`;

// ===== CONTAINER PRINCIPAL =====
export const FieldsContainer = styled.div`
  width: 100%;
  max-width: 100%;
  height: 85%;
  max-height: 85%;
  padding: var(--sp-24);
  flex-direction: column;
  box-sizing: border-box;
  overflow: auto;

  &::-webkit-scrollbar {
    width: calc(var(--u) * 15);
    height: calc(var(--u) * 15);
  }

  &::-webkit-scrollbar-track {
    background: ${(props) => props.theme.colors.modalColor + "90"};
  }

  &::-webkit-scrollbar-thumb {
    background-color: ${(props) => props.theme.colors.selection};
    border-radius: 10px;
    border: 2px solid ${(props) => props.theme.colors.modalColor + "90"};
  }
`;

// ===== CABEÇALHO =====
export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: var(--sp-24);
  gap: var(--sp-16);
  flex-wrap: wrap;
  animation: ${fadeIn} 0.3s ease;
`;

export const HeaderContent = styled.div`
  flex: 1;
  min-width: calc(var(--u) * 260);
`;

export const HeaderTitle = styled.h1`
  display: flex;
  align-items: center;
  gap: var(--sp-12);
  margin: 0 0 var(--sp-6) 0;
  font-size: var(--fs-3xl);
  font-weight: 700;
  color: ${(props) => props.theme.colors.text};
`;

export const HeaderSubtitle = styled.p`
  margin: 0;
  font-size: var(--fs-base);
  color: ${(props) => props.theme.colors.label};
`;

// ===== CARTÃO DE UMA FILIAL =====
export const FilialCard = styled.div`
  background-color: ${(props) => props.theme.colors.modalColor};
  border: 1px solid ${(props) => props.theme.colors.selection + "40"};
  border-radius: 12px;
  padding: var(--sp-20);
  margin-bottom: var(--sp-20);
  animation: ${fadeIn} 0.3s ease;
`;

export const FilialTitulo = styled.h3`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  margin: 0;
  font-size: var(--fs-lg);
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
`;

export const FilialSubtitulo = styled.div`
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.label};
  margin-top: var(--sp-4);
  margin-bottom: var(--sp-16);
`;

// ===== OS DOIS BLOCOS (na entrega / no app) =====
export const BlocosGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(calc(var(--u) * 300), 1fr));
  gap: var(--sp-16);
`;

export const Bloco = styled.div`
  background-color: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.selection + "30"};
  border-radius: 8px;
  padding: var(--sp-16);
`;

export const BlocoTitulo = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  font-size: var(--fs-md);
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: var(--sp-12);
`;

export const Linha = styled.label<{ $desabilitada?: boolean }>`
  display: flex;
  align-items: center;
  gap: var(--sp-8);
  padding: var(--sp-6) 0;
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.text};
  cursor: ${(props) => (props.$desabilitada ? "not-allowed" : "pointer")};
  opacity: ${(props) => (props.$desabilitada ? 0.5 : 1)};
`;

export const Caixa = styled.input`
  width: var(--sp-16);
  height: var(--sp-16);
  cursor: inherit;
  accent-color: ${(props) => props.theme.colors.selection};
`;

export const LinhaMeios = styled.div<{ $desabilitada?: boolean }>`
  padding-left: var(--sp-24);
  border-left: 2px solid ${(props) => props.theme.colors.selection + "30"};
  margin-left: var(--sp-6);
  opacity: ${(props) => (props.$desabilitada ? 0.45 : 1)};
`;

export const Seletor = styled.select`
  padding: var(--sp-6) var(--sp-10);
  border-radius: 6px;
  border: 1px solid ${(props) => props.theme.colors.selection + "60"};
  background-color: ${(props) => props.theme.colors.modalColor};
  color: ${(props) => props.theme.colors.text};
  font-size: var(--fs-md);
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

// ===== RODAPÉ DE AÇÃO =====
export const AcaoRodape = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-12);
  flex-wrap: wrap;
  margin-top: var(--sp-16);
  padding-top: var(--sp-16);
  border-top: 1px solid ${(props) => props.theme.colors.selection + "30"};
`;

export const RotuloCampo = styled.span`
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.label};
`;

export const Button = styled.button<{ color?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-10) var(--sp-20);
  border-radius: 8px;
  border: none;
  background-color: ${(props) => props.color || props.theme.colors.selection};
  color: ${(props) => props.theme.colors.textOnPrimary};
  font-weight: 600;
  font-size: var(--fs-md);
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  gap: var(--sp-6);

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px ${(props) => props.theme.colors.selection + "40"};
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
    transform: none;
  }
`;

// ===== AVISO INLINE =====
// Vive aqui, na própria aba, e não no <MessageBox /> do Zustand: o MessageBox
// não está montado na tela de Configuração (só em Login e Register), então
// setMessageZustand não apareceria para ninguém.
export const Aviso = styled.div<{ $tom: "ok" | "erro" | "atencao" }>`
  margin-top: var(--sp-12);
  padding: var(--sp-10) var(--sp-16);
  border-radius: 8px;
  font-size: var(--fs-md);
  line-height: 1.4;
  border: 1px solid
    ${(props) =>
      props.$tom === "erro"
        ? "#be3028"
        : props.$tom === "atencao"
        ? "#8f5e07"
        : "#26792f"};
  background-color: ${(props) =>
    props.$tom === "erro"
      ? "#be302818"
      : props.$tom === "atencao"
      ? "#8f5e0718"
      : "#26792f18"};
  color: ${(props) => props.theme.colors.text};
  animation: ${fadeIn} 0.3s ease;
`;

// ===== SITUAÇÃO DA FILA =====
export const FilaCaixa = styled.div`
  margin-top: var(--sp-12);
  padding: var(--sp-12) var(--sp-16);
  border-radius: 8px;
  background-color: ${(props) => props.theme.colors.background};
  border: 1px solid ${(props) => props.theme.colors.selection + "30"};
  font-size: var(--fs-md);
  color: ${(props) => props.theme.colors.label};
`;

export const FilaTitulo = styled.div`
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: var(--sp-6);
`;

export const FilaItem = styled.div`
  display: flex;
  align-items: center;
  gap: var(--sp-6);
  padding: var(--sp-2) 0;
`;

// ===== ESTADOS =====
export const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--sp-16);
  padding: calc(var(--u) * 60);
  color: ${(props) => props.theme.colors.label};
`;

export const LoadingSpinner = styled.div`
  width: var(--sp-40);
  height: var(--sp-40);
  border: 4px solid ${(props) => props.theme.colors.selection + "30"};
  border-top-color: ${(props) => props.theme.colors.selection};
  border-radius: 50%;
  animation: ${spin} 0.9s linear infinite;
`;

export const EmptyState = styled.div`
  border: 2px dashed ${(props) => props.theme.colors.selection + "50"};
  border-radius: 12px;
  padding: calc(var(--u) * 60) var(--sp-40);
  text-align: center;
  animation: ${fadeIn} 0.4s ease;
`;

export const EmptyStateIcon = styled.div`
  font-size: var(--fs-7xl);
  margin-bottom: var(--sp-16);
  animation: ${pulse} 2s ease-in-out infinite;
`;

export const EmptyStateTitle = styled.div`
  font-size: var(--fs-xl);
  font-weight: 600;
  color: ${(props) => props.theme.colors.text};
  margin-bottom: var(--sp-8);
`;

export const EmptyStateDescription = styled.div`
  font-size: var(--fs-base);
  color: ${(props) => props.theme.colors.label};
`;

export const MaterialIcons = styled.span`
  font-family: "Material Icons";
  font-weight: normal;
  font-style: normal;
  font-size: var(--fs-xl);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
`;
